import { useEffect, useRef, useState } from 'react';
import { Button } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import layoutStore from '../stores/layoutStore';
import usePersistStore from '../stores/persistStore';
import { AnimatePresence, motion } from 'framer-motion';
import useStore from '../stores/store';
import {
    CloseCircleIcon,
    DotsIcons,
    EditPenIcon,
    PlusSmallIcon,
    TrashIcon
} from './UiElements';
import { ButtonGroup, IconButton, useBoolean } from '@chakra-ui/react';
import { useLongPress } from '@uidotdev/usehooks';
import { scrollToGroupContainer } from './Groups';
import { useHotkeys } from 'react-hotkeys-hook';

const Categories = () => {
    const setSelectedCategory = usePersistStore(
        (state) => state.setSelectedCategory
    );
    const setGroupsToShow = useStore((state) => state.setGroupsToShow);

    const selectedCategory = usePersistStore((state) => state.selectedCategory);
    const workspaceZoom = layoutStore((state) => state.workspaceZoom);
    const categories = useStore((state) => state.categories);

    const tabGroups = useStore((state) => state.tabGroups);
    const hasHydrated = usePersistStore((state) => state._hasHydrated);
    const setCatModal = layoutStore((state) => state.setCatModal);

    //const setGroupsToShow = useStore((state) => state.setGroupsToShow);
    //const selectedCategory = usePersistStore((state) => state.selectedCategory);
    useEffect(() => {
        if (!hasHydrated) {
            usePersistStore.persist.rehydrate();
        } else if (tabGroups) {
            if (categories.length <= selectedCategory) {
                setSelectedCategory(0);
                setGroupsToShow(0);
            } else {
                setGroupsToShow(selectedCategory);
            }
        }
    }, [hasHydrated, tabGroups]);

    //const [hovered, setHovered] = useState(null);
    const [editMode, setEditMode] = useState(false);

    const editCategories = () => {
        setEditMode(true);
    };

    const closeEditMode = () => {
        setEditMode(false);
    };

    const hoverBlockBg = useColorModeValue('#454c5910', '#454c5925');
    const activeBlockBg = useColorModeValue('#454c5928', '#454c5945');
    const selectedBg = useColorModeValue(
        'linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)',
        'linear-gradient(178deg, #52595D 0%, #40484F 100%)'
    );
    const selectedShadow = useColorModeValue(
        'rgba(70, 94, 193, 0.25) -1px -1px 1px 0px inset',
        'rgb(36 37 42 / 25%) -1px -1px 1px 0px inset'
    );
    const categoryRailRef = useRef<HTMLDivElement>(null);
    const categoryRefs = useRef<Array<HTMLElement | null>>([]);

    useEffect(() => {
        const categoryRail = categoryRailRef.current;
        const selectedElement = categoryRefs.current[selectedCategory];

        if (
            !categoryRail ||
            !selectedElement ||
            categoryRail.scrollWidth <= categoryRail.clientWidth
        ) {
            return;
        }

        const reduceMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        selectedElement.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center'
        });
    }, [selectedCategory, categories?.length]);

    const changeCat = (i) => {
        setSelectedCategory(i);
        setGroupsToShow(i);
        scrollToGroupContainer('start');
    };

    useHotkeys(
        ['1', '2', '3', '4', '5', '6', '7', '8'],
        (_, handler) => {
            const key = handler.keys[0];
            const index = parseInt(key, 10) - 1;
            if (index >= 0 && index < categories.length) {
                changeCat(index);
            }
        },
        {},
        [selectedCategory, categories]
    );

    const attrs = useLongPress(
        () => {
            setCatModal(true);
        },
        {
            threshold: 420
        }
    );

    return (
        <EditCatsWrapper
            editCategories={editCategories}
            closeEditMode={closeEditMode}
            editMode={editMode}
        >
            <Flex
                data-testid="category-rail"
                ref={categoryRailRef}
                //onMouseLeave={() => setHovered(null)}
                bg={'categoriesBg'}
                rounded="8px"
                boxShadow={'inset 0 1px 4px 0 #454c5914'}
                w={{ base: '100%', md: 'auto' }}
                minW={0}
                overflowX={{ base: 'auto', md: 'hidden' }}
                overflowY="hidden"
                scrollSnapType={{ base: 'x proximity', md: 'none' }}
                sx={{
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' }
                }}
                {...attrs}
            >
                {categories?.map((c, i) => (
                    /* editMode ? (
                        <EditCatNameButton
                            key={c.id}
                            selectedCategory={selectedCategory}
                            i={i}
                            c={c}
                        />
                    ) : (*/
                    <Flex
                        as="button"
                        ref={(element) => {
                            categoryRefs.current[i] = element;
                        }}
                        cursor={'pointer'}
                        key={c.id}
                        //rounded={rounded[i]}
                        my="auto"
                        px="18px"
                        py={{ base: '10px', md: '6px' }}
                        maxH={{ base: '44px', md: '32px' }}
                        minH={{ base: '44px', md: '32px' }}
                        flexShrink={0}
                        scrollSnapAlign="center"
                        whiteSpace="nowrap"
                        rounded={'8px'}
                        onClick={() => changeCat(i)}
                        aria-current={
                            selectedCategory === i ? 'page' : undefined
                        }
                        //onFocus={() => setHovered(i)}
                        //onMouseEnter={() => setHovered(i)}
                        //onMouseLeave={() => setHovered(null)}
                        //pos={'relative'}
                        bg={selectedCategory === i && selectedBg}
                        boxShadow={selectedCategory === i && selectedShadow}
                        _hover={{
                            filter: 'brightness(1.05)',
                            bg: selectedCategory !== i ? hoverBlockBg : ''
                        }}
                        _active={{
                            transform: 'scale(0.96)',
                            borderRadius: '7px'
                            //filter: 'brightness(0.975)',
                            //bg: selectedCategory !== i ? activeBlockBg : ''
                        }}
                        transition={'all 0.12s, box-shadow 0s'}
                    >
                        {/* {hovered === i && (
                        <motion.div
                            layoutId="hover"
                            transition={{ duration: 0.02, ease: 'easeOut' }}
                            whileTap={{
                                scale: 0.9625,
                                borderRadius: '6px',
                                transition: { duration: 0.1 }
                            }}
                            style={{
                                position: 'absolute',
                                height: '28px',
                                top: 2,
                                left: 2,
                                background: hoverBlockBg,
                                width: 'calc(100% - 4px)',
                                borderRadius: '7px',
                                zIndex: 1,
                                willChange: 'transform'
                            }}
                        />
                    )}
                    {selectedCategory === i && (
                        <motion.div
                            layoutId="sel"
                            transition={{ duration: 0.04, ease: 'easeOut' }}
                            whileHover={{ filter: 'brightness(1.05)' }}
                            whileTap={{
                                scale: 0.9625,
                                borderRadius: '6px',
                                filter: 'brightness(0.98)',
                                transition: { duration: 0.1 }
                            }}
                            style={{
                                position: 'absolute',
                                height: '28px',
                                top: 2,
                                left: 2,
                                background: selectedBg,
                                width: 'calc(100% - 4px)',
                                borderRadius: '7px',
                                zIndex: 3,
                                boxShadow: selectedShadow
                            }}
                        />
                    )} */}
                        <motion.p
                            key={selectedCategory}
                            style={{
                                margin: 0,
                                opacity:
                                    selectedCategory === i
                                        ? 1
                                        : c.name
                                        ? 0.9
                                        : 0.4,
                                fontWeight: '600',
                                fontSize: '14px',
                                zIndex: 4,
                                userSelect: 'none',
                                pointerEvents: 'none',
                                transition: '0.14s',
                                color:
                                    selectedCategory === i
                                        ? '#FFF'
                                        : 'var(--chakra-colors-catText)'
                            }}
                        >
                            {c.name || 'Untitled'}
                        </motion.p>
                    </Flex>
                ))}
            </Flex>
        </EditCatsWrapper>
    );
};

export default Categories;

const buttonProps = {
    rounded: '8px',
    bg: 'categoriesBg',
    _active: {
        transform: 'scale(0.96)'
    }
};

const EditCatsWrapper = ({
    editCategories,
    closeEditMode,
    editMode,
    createNewCategory,
    children
}: any) => {
    const [hoverCats, hover] = useBoolean();
    const hoverButtonBg = useColorModeValue('#DDE1E6', '#2C3034');
    const setCatModal = layoutStore((state) => state.setCatModal);
    const showCatModal = layoutStore((state) => state.showCatModal);

    const openCatModal = () => {
        setCatModal(true);
    };

    return (
        <Flex
            w={{ base: '100%', md: 'auto' }}
            minW={0}
            onMouseEnter={hover.on}
            onMouseOver={hover.on}
            onMouseLeave={hover.off}
        >
            {children}
            <AnimatePresence>
                {hoverCats && (
                    <motion.div
                        initial={{ x: -8, opacity: 0 }}
                        animate={{
                            x: 0,
                            opacity: 1,
                            transition: { delay: 0.16, duration: 0.12 }
                        }}
                        exit={{
                            x: 0,
                            opacity: 0,
                            transition: {
                                delay: showCatModal ? 0 : 0.26,
                                duration: 0.12
                            }
                        }}
                    >
                        {/* <ButtonGroup size="sm" isAttached ml="8px"> */}
                        {/* {editMode ? (
                                <IconButton
                                    onClick={closeEditMode}
                                    aria-label="Close edit mode"
                                    icon={<CloseCircleIcon size={'14px'} />}
                                    _hover={{
                                        bg: hoverButtonBg
                                    }}
                                    {...buttonProps}
                                />
                            ) : ( */}

                        <IconButton
                            size="sm"
                            ml="8px"
                            onClick={openCatModal}
                            aria-label="Edit categories"
                            icon={<EditPenIcon />}
                            _hover={{
                                bg: hoverButtonBg
                            }}
                            {...buttonProps}
                        />
                        {/* <IconButton
                                    onClick={createNewCategory}
                                    aria-label="Add category"
                                    icon={<PlusSmallIcon />}
                                    _hover={{
                                        bg: hoverButtonBg
                                    }}
                                    {...buttonProps}
                                />
                            
                        </ButtonGroup> */}
                    </motion.div>
                )}
            </AnimatePresence>
        </Flex>
    );
};

/* const EditCatNameButton = ({ selectedCategory, i, c }) => {
    const hoverBlockBg = useColorModeValue('#454c5910', '#454c5925');
    const activeBlockBg = useColorModeValue('#454c5928', '#454c5945');
    const selectedBg = useColorModeValue(
        'linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)',
        'linear-gradient(178deg, #52595D 0%, #40484F 100%)'
    );
    const selectedShadow = useColorModeValue(
        '0 2px 3px -3px #287AD5, rgba(70, 94, 193, 0.25) -1px -1px 1px 0px inset',
        'rgb(41 44 47) 0px 2px 3px -3px, rgb(36 37 42 / 25%) -1px -1px 1px 0px inset'
    );
    const popoverShadowLight = useColorModeValue(
        '0px 0px 1px rgba(166,166,167,0.52), 0px 10px 12px -2px rgba(166,166,167,0.4), 0px 24px 54px 3px rgba(166,166,167,0.28)',
        '0px 0px 1px rgba(19,20,24,0.95), 0px 10px 12px -2px rgba(19,20,24,0.74), 0px 24px 54px 3px rgba(19,20,24,0.65)'
    );
    const submitNewCatName = (newName) => {
        //TODO LOGIC update category name
    };

    return (
        <Popover trigger="hover">
            <PopoverTrigger>
                <Flex
                    alignItems={'center'}
                    my="auto"
                    p="0px 18px"
                    maxH={'32px'}
                    minH={'32px'}
                    rounded={'8px'}
                    bg={selectedCategory === i && selectedBg}
                    boxShadow={selectedCategory === i && selectedShadow}
                    transition={'0.12s'}
                    _hover={{
                        filter: 'brightness(1.05)',
                        bg: selectedCategory !== i ? hoverBlockBg : '',
                        opacity: 0.8
                    }}
                    role="group"
                    cursor={'grab'}
                    _active={{ cursor: 'grabbing' }}
                    opacity={0.675}
                >
                    <Text
                        m="0"
                        fontSize="14px"
                        fontWeight="600"
                        //cursor="text"
                        userSelect={'none'}
                        opacity={
                            selectedCategory === i ? 1 : c.name ? 0.9 : 0.4
                        }
                        color={
                            selectedCategory === i
                                ? '#FFF'
                                : 'var(--chakra-colors-catText)'
                        }
                    >
                        {c.name || 'Untitled'}
                    </Text>
                </Flex>
            </PopoverTrigger>
            <PopoverContent
                maxW="200px"
                boxShadow={popoverShadowLight}
                zIndex={999999}
                rounded="10px"
                motionProps={{ transition: { duration: 0.08 } }}
            >
                <PopoverArrow />
                <PopoverBody my="4px" display={'flex'}>
                    <Input
                        rounded={'6px'}
                        size="sm"
                        variant="filled"
                        placeholder="Untitled"
                        value={c.name}
                        onSubmit={(e) => submitNewCatName(e)}
                    />
                    <IconButton
                        ml="8px"
                        size="sm"
                        aria-label="Delete category"
                        icon={<TrashIcon />}
                        rounded={'6px'}
                    />
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}; */
